import { createSelector } from 'reselect';
import { StoreState } from 'store/types';
import { ReconciliationState } from './types';
import { TransactionTypeFilter } from './models';

const selectReconciliation = (app: StoreState): ReconciliationState =>
  app.reconciliation;

export const getReconciliationLoading = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['loading'] => s.loading
);

export const getReconciliationGenerating = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['generating'] => s.generating
);

export const getReconciliationComplete = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['generationComplete'] =>
    s.generationComplete
);

export const getReconciliationReportId = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['reportId'] => s.reportId
);

export const getReconciliationDataStatus = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['dataStatus'] => s.dataStatus
);

export const getReconciliationRetryAfterSeconds = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['retryAfterSeconds'] =>
    s.retryAfterSeconds
);

export const getReconciliationTransactionsSize = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['transactionsSize'] =>
    s.transactionsSize
);

export const getReconciliationExceptionsSize = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['exceptionsSize'] =>
    s.exceptionsSize
);

export const getReconciliationOthersSize = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['othersSize'] => s.othersSize
);

export const getReconciliationTotalTax = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['totalTax'] => s.totalTax
);

export const getReconciliationTotalTaxTransactions = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['totalTaxTransactions'] =>
    s.totalTaxTransactions
);

export const getReconciliationTotalTaxExceptions = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['totalTaxExceptions'] =>
    s.totalTaxExceptions
);

export const getReconciliationTotalTaxOthers = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['totalTaxOthers'] =>
    s.totalTaxOthers
);

export const getReconciliationHeaderLoading = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['headerLoading'] =>
    s.headerLoading
);

export const getReconciliationHeaderData = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['headerData'] => s.headerData
);

export const getReconciliationQueryLoading = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['queryLoading'] =>
    s.queryLoading
);

export const getReconciliationJournals = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['journals'] => s.journals
);

export const getReconciliationJournalsByType = (
  transactionType: TransactionTypeFilter
) =>
  createSelector(
    selectReconciliation,
    (s: ReconciliationState): ReconciliationState['journals'] | undefined =>
      s.journalsByType[transactionType]
  );

export const getReconciliationFiltersLoading = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['filtersLoading'] =>
    s.filtersLoading
);

export const getReconciliationLogsLoading = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['logsLoading'] => s.logsLoading
);

export const getReconciliationLogs = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['logs'] => s.logs
);

export const getReconciliationLogsPageSize = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['logsPageSize'] =>
    s.logsPageSize
);

export const getReconciliationLogsTotalElements = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['logsTotalElements'] =>
    s.logsTotalElements
);

export const getSaveReconciliationReportLoading = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['saveReportLoading'] =>
    s.saveReportLoading
);

export const getSaveReconciliationReportSuccess = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['saveReportSuccess'] =>
    s.saveReportSuccess
);

export const getReconciliationMappingCodes = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['mappingCodes'] =>
    s.mappingCodes
);

export const getReconciliationAccounts = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['accounts'] => s.accounts
);

export const getReconciliationTaxRates = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['taxRates'] => s.taxRates
);

export const getReconciliationError = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['error'] => s.error
);

export const getTaxBalanceUpdating = createSelector(
  selectReconciliation,
  (s: ReconciliationState): boolean => s.taxBalanceUpdating
);

export const getViewReconciliationData = createSelector(
  selectReconciliation,
  (s: ReconciliationState): ReconciliationState['viewReconciliationData'] =>
    s.viewReconciliationData
);

export const getLoadReconciliationTransactionsLoading = createSelector(
  selectReconciliation,
  (s: ReconciliationState): boolean =>
    s.loading && s.generating && !s.generationComplete
);
